import { logger } from "../utils/logger";

export type BehaviorTrainedModel = {
  id: string;
  name: string;
  robotKey: string;
  robotLabel: string;
  obsDim: number;
  actionDim: number;
  onnxUrl: string;
  vecnormalizeUrl: string;
  notes?: string;
};

type RemoteCatalog = {
  schemaVersion?: number;
  models?: Array<{
    id?: string;
    name?: string;
    robotKey?: string;
    obsDim?: number;
    actionDim?: number;
    onnxPath?: string;
    vecnormalizePath?: string;
    notes?: string;
  }>;
};

const CRITTER_MODELS_BASE = "https://raw.githubusercontent.com/CritterEngine/critter-models/main";
const CRITTER_MODELS_INDEX_URL = `${CRITTER_MODELS_BASE}/index.json`;
const MODEL_ID_ALIASES: Record<string, string> = {
  spot_walk: "spot-walk-locomotion",
  spot_efficiency_200k: "spot-walk-locomotion",
  "spot-efficiency-200k": "spot-walk-locomotion",
  go2_walk: "go2-walk-stagef",
};

function robotLabelForKey(robotKey: string): string {
  if (robotKey === "boston_dynamics_spot") return "Boston Dynamics Spot";
  if (robotKey === "unitree_go2") return "Unitree Go2";
  return robotKey || "Unknown robot";
}

function displayNameForModel(rawId: string, rawName: string): string {
  if (rawId === "go2-walk-stagef") return "Go2 Walk";
  if (rawId === "spot-walk-locomotion") return "Spot Walk";
  return rawName || rawId;
}

const FALLBACK_BEHAVIOR_TRAINED_MODELS: BehaviorTrainedModel[] = [
  {
    id: "go2-walk-stagef",
    name: "Go2 Walk",
    robotKey: "unitree_go2",
    robotLabel: "Unitree Go2",
    obsDim: 47,
    actionDim: 12,
    onnxUrl: `${CRITTER_MODELS_BASE}/models/go2/ppo_go2_locomotion_stageF_speedpolish_300k_auto.onnx`,
    vecnormalizeUrl: `${CRITTER_MODELS_BASE}/models/go2/ppo_go2_locomotion_stageF_speedpolish_300k_auto.vecnormalize.json`,
    notes: "Go2 Stage F speed-polish (300k auto) with gated efficiency shaping.",
  },
  {
    id: "spot-walk-locomotion",
    name: "Spot Walk",
    robotKey: "boston_dynamics_spot",
    robotLabel: "Boston Dynamics Spot",
    obsDim: 47,
    actionDim: 12,
    onnxUrl: `${CRITTER_MODELS_BASE}/models/spot/ppo_spot_locomotion_stageE_polish_500k.onnx`,
    vecnormalizeUrl: `${CRITTER_MODELS_BASE}/models/spot/ppo_spot_locomotion_stageE_polish_500k.vecnormalize.json`,
    notes: "Spot Stage E polish model with gait-balance penalties.",
  },
];

let cachedBehaviorTrainedModels: BehaviorTrainedModel[] = FALLBACK_BEHAVIOR_TRAINED_MODELS;
let behaviorTrainedModelsLoadPromise: Promise<BehaviorTrainedModel[]> | null = null;
let behaviorTrainedModelsLoaded = false;

function mergeBehaviorTrainedModels(models: BehaviorTrainedModel[]): BehaviorTrainedModel[] {
  const merged = new Map<string, BehaviorTrainedModel>();
  // Keep local fallback entries authoritative for known canonical IDs
  // so stale remote catalog entries cannot regress critical model URLs.
  [...models, ...FALLBACK_BEHAVIOR_TRAINED_MODELS].forEach((model) => {
    const canonicalId = canonicalBehaviorTrainedModelId(model.id);
    if (!canonicalId) return;
    merged.set(canonicalId, {
      ...model,
      id: canonicalId,
      name: displayNameForModel(canonicalId, model.name),
      robotLabel: robotLabelForKey(model.robotKey),
    });
  });
  return Array.from(merged.values());
}

function normalizeRemoteCatalog(input: unknown): BehaviorTrainedModel[] {
  const raw = (input && typeof input === "object" ? input : {}) as RemoteCatalog;
  const models = Array.isArray(raw.models) ? raw.models : [];
  const normalized = models
    .map((entry) => {
      const rawId = typeof entry?.id === "string" ? entry.id.trim() : "";
      const id = canonicalBehaviorTrainedModelId(rawId);
      const robotKey = typeof entry?.robotKey === "string" ? entry.robotKey.trim() : "";
      const onnxPath = typeof entry?.onnxPath === "string" ? entry.onnxPath.trim() : "";
      const vecnormalizePath =
        typeof entry?.vecnormalizePath === "string" ? entry.vecnormalizePath.trim() : "";
      if (!id || !robotKey || !onnxPath || !vecnormalizePath) return null;
      const nameRaw = typeof entry?.name === "string" ? entry.name.trim() : "";
      return {
        id,
        name: displayNameForModel(id, nameRaw),
        robotKey,
        robotLabel: robotLabelForKey(robotKey),
        obsDim: Number.isFinite(entry?.obsDim) ? Number(entry.obsDim) : 0,
        actionDim: Number.isFinite(entry?.actionDim) ? Number(entry.actionDim) : 0,
        onnxUrl: `${CRITTER_MODELS_BASE}/${onnxPath.replace(/^\//, "")}`,
        vecnormalizeUrl: `${CRITTER_MODELS_BASE}/${vecnormalizePath.replace(/^\//, "")}`,
        notes: typeof entry?.notes === "string" ? entry.notes : undefined,
      } satisfies BehaviorTrainedModel;
    })
    .filter((model) => model != null);
  return normalized.length
    ? mergeBehaviorTrainedModels(normalized)
    : FALLBACK_BEHAVIOR_TRAINED_MODELS;
}

export function canonicalBehaviorTrainedModelId(modelId: string | null | undefined): string {
  const raw = typeof modelId === "string" ? modelId.trim() : "";
  return MODEL_ID_ALIASES[raw] || raw;
}

export function getBehaviorTrainedModels(): BehaviorTrainedModel[] {
  return cachedBehaviorTrainedModels;
}

export async function loadBehaviorTrainedModels(): Promise<BehaviorTrainedModel[]> {
  if (behaviorTrainedModelsLoaded) return cachedBehaviorTrainedModels;
  if (behaviorTrainedModelsLoadPromise) return behaviorTrainedModelsLoadPromise;
  behaviorTrainedModelsLoadPromise = fetch(CRITTER_MODELS_INDEX_URL)
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      const normalized = normalizeRemoteCatalog(json);
      cachedBehaviorTrainedModels = normalized;
      behaviorTrainedModelsLoaded = true;
      return normalized;
    })
    .catch((error) => {
      logger.warn(
        "[behavior] Failed to load critter-models index.json; using fallback catalog",
        error
      );
      cachedBehaviorTrainedModels = FALLBACK_BEHAVIOR_TRAINED_MODELS;
      return cachedBehaviorTrainedModels;
    })
    .finally(() => {
      behaviorTrainedModelsLoadPromise = null;
    });
  return behaviorTrainedModelsLoadPromise;
}

export function getBehaviorTrainedModel(
  modelId: string | null | undefined
): BehaviorTrainedModel | null {
  const canonicalId = canonicalBehaviorTrainedModelId(modelId);
  if (!canonicalId) return null;
  return cachedBehaviorTrainedModels.find((model) => model.id === canonicalId) ?? null;
}

void loadBehaviorTrainedModels();
